"use client";

import {
  Listbox as HeadlessListbox,
  ListboxButton as HeadlessListboxButton,
  ListboxOption as HeadlessListboxOption,
  ListboxOptions as HeadlessListboxOptions,
  ListboxSelectedOption as HeadlessListboxSelectedOption,
  Transition as HeadlessTransition,
} from "@headlessui/react";
import clsx from "clsx";
import { Fragment, useState } from "react";

/**
 * ListboxWithSections
 *
 * Supports both grouped (sectioned) and flat options.
 *
 * Usage:
 * <ListboxWithSections
 *   value={...}
 *   onChange={...}
 *   options={[
 *     { label: "Fruits", sticky: true, options: [ { value: "apple", label: "Apple" }, ... ] },
 *     { label: "Vegetables", sticky: true, options: [ { value: "carrot", label: "Carrot" }, ... ] }
 *   ]}
 *   placeholder="Select an item"
 * />
 *
 * Or with a flat array:
 * <ListboxWithSections
 *   value={...}
 *   onChange={...}
 *   options={[
 *     { value: "apple", label: "Apple" },
 *     { value: "banana", label: "Banana" }
 *   ]}
 *   placeholder="Select an item"
 * />
 */

function isGroupedOptions(options) {
  const isGrouped =
    Array.isArray(options) &&
    options.length > 0 &&
    options[0] &&
    Array.isArray(options[0].options);
  console.log({ isGrouped, options });
  return isGrouped;
}

function ListboxSection({ label, sticky, children, collapsed, onToggle }) {
  return (
    <div className="relative">
      <div
        className={clsx(
          "flex items-center px-3 py-1 font-semibold text-zinc-700 dark:text-zinc-200 z-10 cursor-pointer select-none min-h-10 shadow-sm bg-gray-50",
          sticky && "sticky top-0 border-b"
        )}
        onClick={onToggle}
        role="button"
        aria-expanded={!collapsed}
        tabIndex={0}
      >
        <span className="flex-1 text-sm">{label}</span>
        <svg
          className={clsx(
            "transition-transform size-4 ml-2",
            collapsed ? "rotate-0" : "rotate-90"
          )}
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>{!collapsed && children}</div>
    </div>
  );
}

export function ListboxWithSections({
  className,
  placeholder,
  autoFocus,
  "aria-label": ariaLabel,
  options,
  ...props
}) {
  // Collapsed state per section label
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (label) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const renderOptions = () => {
    if (isGroupedOptions(options)) {
      return options.map((group) => (
        <ListboxSection
          key={group.label}
          label={group.labelNode ?? group.label}
          sticky={group.sticky}
          collapsed={!!collapsedSections[group.label]}
          onToggle={() => toggleSection(group.label)}
        >
          {group.options.map((opt) => (
            <ListboxOption
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
            >
              {opt.icon && opt.icon}
              <ListboxLabel>{opt.label}</ListboxLabel>
            </ListboxOption>
          ))}
        </ListboxSection>
      ));
    }
    return options.map((opt) => (
      <ListboxOption
        key={opt.value}
        value={opt.value}
        disabled={opt.disabled}
      >
        {opt.icon && opt.icon}
        <ListboxLabel>{opt.label}</ListboxLabel>
      </ListboxOption>
    ));
  };

  const allOptions = isGroupedOptions(options)
    ? options.flatMap((group) => group.options)
    : options;

  const renderAllOptions = allOptions.map((opt) => (
    <ListboxOption
      key={opt.value}
      value={opt.value}
    >
      {opt.icon && opt.icon}
      <ListboxLabel>{opt.label}</ListboxLabel>
    </ListboxOption>
  ));

  return (
    <HeadlessListbox
      {...props}
      className="w-full"
      multiple={false}
    >
      <HeadlessListboxButton
        autoFocus={autoFocus}
        data-slot="control"
        aria-label={ariaLabel}
        className={clsx(
          className,
          "group relative block w-full",
          "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow",
          "dark:before:hidden",
          "focus:outline-none",
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:data-[focus]:ring-2 sm:after:data-[focus]:ring-blue-500",
          "data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none"
        )}
      >
        <HeadlessListboxSelectedOption
          as="span"
          options={renderAllOptions}
          placeholder={
            placeholder && (
              <span className="block truncate text-zinc-500">
                {placeholder}
              </span>
            )
          }
          className={clsx(
            "relative block w-full appearance-none rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]",
            "min-h-11 sm:min-h-9",
            "pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.7)-1px)] sm:pl-[calc(theme(spacing.3)-1px)]",
            "text-left text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",
            "border border-zinc-950/10 group-data-[active]:border-zinc-950/20 group-data-[hover]:border-zinc-950/20 dark:border-white/10 dark:group-data-[active]:border-white/20 dark:group-data-[hover]:border-white/20",
            "bg-transparent dark:bg-white/5",
            "group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600",
            "group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15"
          )}
        />
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className="size-5 stroke-zinc-500 group-data-[disabled]:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400 forced-colors:stroke-[CanvasText]"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </HeadlessListboxButton>
      <HeadlessTransition
        as={Fragment}
        leave="transition-opacity duration-100 ease-in pointer-events-none"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <HeadlessListboxOptions
          as="div"
          anchor={{
            to: "bottom center",
            offset: "0rem",
            gap: "0.25rem",
          }}
          className={clsx(
            "[--anchor-offset:-1.625rem] [--anchor-padding:theme(spacing.4)] sm:[--anchor-offset:-1.375rem]",
            "isolate min-w-[calc(var(--button-width))] overflow-x-auto select-none scroll-py-1 rounded-xl",
            "outline outline-1 outline-transparent focus:outline-none",
            "overflow-y-scroll overscroll-contain",
            "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",
            "shadow-lg ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10"
          )}
        >
          {renderOptions()}
        </HeadlessListboxOptions>
      </HeadlessTransition>
    </HeadlessListbox>
  );
}

export function ListboxOption({ children, className, ...props }) {
  // Forward all props, including value and disabled
  const sharedClasses = clsx(
    "flex min-w-0 items-center",
    "[&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-zinc-500 [&>[data-slot=icon]]:group-data-[focus]/option:text-white sm:[&>[data-slot=icon]]:size-4 forced-colors:[&>[data-slot=icon]]:text-[CanvasText] forced-colors:[&>[data-slot=icon]]:group-data-[focus]/option:text-[Canvas]",
    "[&>[data-slot=avatar]]:size-6 sm:[&>[data-slot=avatar]]:size-5"
  );

  return (
    <HeadlessListboxOption
      as={Fragment}
      {...props}
    >
      {({ selectedOption }) => {
        if (selectedOption) {
          return (
            <div className={clsx(className, sharedClasses)}>{children}</div>
          );
        }
        return (
          <div
            className={clsx(
              "group/option grid cursor-default grid-cols-[theme(spacing.5),1fr] items-baseline gap-x-1.5 rounded-lg py-2.5 pl-2.5 pr-3.5 sm:grid-cols-[theme(spacing.4),1fr] sm:py-1.5 sm:pl-2 sm:pr-3",
              "text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",
              "outline-none data-[focus]:bg-blue-500 data-[focus]:text-white",
              "forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText]",
              "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
              "z-50"
            )}
          >
            <svg
              className="relative hidden size-5 self-center stroke-current group-data-[selected]/option:inline sm:size-4"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 8.5l3 3L12 4"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className={clsx(className, sharedClasses, "col-start-2")}>
              {children}
            </span>
          </div>
        );
      }}
    </HeadlessListboxOption>
  );
}

// Optional: Helper components for label/description matching your previous API

export function ListboxLabel({ className, ...props }) {
  return (
    <span
      className={clsx(
        className,
        "ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0"
      )}
      {...props}
    />
  );
}

export function ListboxDescription({ className, children, ...props }) {
  return (
    <span
      className={clsx(
        className,
        "flex flex-1 overflow-hidden text-zinc-500 before:w-2 before:min-w-0 before:shrink group-data-[focus]/option:text-white dark:text-zinc-400"
      )}
      {...props}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}
